import clsx from 'clsx';
import { Link } from '../../navigation/Link';
import type { LinkProps } from '../../navigation/Link';

import styles from './MenuItem.module.scss';

export type Props = {
  /**
   * A string that names the menu item.
   *
   * **Should only be defined if the item's contents don't provide a useful accessible name and no visible content is available to provide the accessible name**.
   */
  'aria-label'?: string;
  /**
   * An ID that Identifies the element that names the menu item.
   *
   * **Should only be defined if the item's contents don't provide a useful accessible name and a different element's contents can be used to provide the accessible name**.
   */
  'aria-labelledby'?: string;
  /**
   * What to render in the `MenuItem`.
   */
  children: React.ReactNode;
  /**
   * Optional property for overriding styles with CSS-in-JS or defining a custom class.
   * */
  className?: string;
  /** A Component to pass into the `MenuItem` if you'd like to render a different kind of link,
   * like a Router Link, for example
   * Only if the `Tag` is a link
   */
  component?: LinkProps['component'];
  /**
   * Indicates whether the item should be disabled. When an `href` is provided, a non-functional link will be rendered.
   */
  disabled?: boolean;
  /**
   * Optional property which, when supplied, will render `MenuItem` as an `a` tag.
   */
  href?: string;
  /**
   * Whether the item is currently selected. Should be used in circumstances
   * where items are ephemerally chosen.
   */
  isSelected?: boolean;
  /**
   * Optional `id` so the node can be referenced.
   */
  id?: string;
  /**
   * Optional click handler for the `MenuItem`.
   */
  onClick?: (event: React.MouseEvent) => void;
  /**
   * Optional `rel` to be applied to the menu item if it is rendered as an anchor tag and not a button.
   */
  rel?: string;
  /**
   * The optional a11y role for the item. `menuitem` is the semantic equivalent of `<li>`
   *  and should be used when the parent has a role of 'menu',
   *  while 'option' is the semantic equivalent of `<option>`and
   *  should be used when the parent has the role of 'listbox'.
   */
  role?: 'option' | 'menuitem';
};

export const MenuItem = (props: Props) => {
  const ariaLabel = props.isSelected
    ? `${props['aria-label'] || props.children}, selected`
    : props['aria-label'];
  const liRole = props.role ? { role: 'none' } : null;

  const commonProps = {
    'aria-label': ariaLabel,
    className: clsx(styles.root, props.className),
    'data-selected': props.isSelected || undefined,
    'data-zds': true,
    onClick: props.onClick,
    role: props.role,
  };

  if (props.href && !props.disabled) {
    return (
      <li {...liRole} data-zds>
        <Link
          {...commonProps}
          component={props.component}
          color={null}
          href={props.href}
          rel={props.rel}
        >
          <span className={styles.text} data-zds>
            {props.children}
          </span>
        </Link>
      </li>
    );
  }

  return (
    <li {...liRole} data-zds>
      <button {...commonProps} disabled={props.disabled} type="button">
        <span className={styles.text} data-zds>
          {props.children}
        </span>
      </button>
    </li>
  );
};
